import { useState, useEffect } from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { Optimizely } from '@grandvisionhq/api-client';

function trackExperiment(userId, experiment, variation) {
    DataLayer.pushEvent({
        event: '',
        atfEcommerce: null
    });
    DataLayer.pushEvent({
        event: 'experiment_init',
        session: {
            experiments: {
                platform: 'optimizely_sdk',
                experimentId: experiment,
                variationId: variation
            }
        },
        user: {
            id: {
                optimizely_sdk: userId
            }
        }
    });
}

const EXPERIMENT_STORAGE_KEY = 'disabled.ab.tests';
function experimentSuccess(variation) {
    return {
        variation,
        error: null,
        isLoading: false
    };
}
function experimentError(error) {
    return {
        variation: null,
        error,
        isLoading: false
    };
}
function experimentLoading() {
    return {
        variation: null,
        error: null,
        isLoading: true
    };
}
/**
 * We want to prevent A/B testing from running on nightly tests.
 * The tests script will inject a key to localStorage! If the value for the key is "true",
 * we want to disable the A/B testing.
 */ function shouldDisableExperiments() {
    if (typeof window === 'undefined') {
        return true;
    }
    return localStorage.getItem(EXPERIMENT_STORAGE_KEY) === 'true';
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
// A userId to Experiment mapping
const experimentCache = {};
/**
 * usePrepareExperiment hook should be used for pre-caching the experiment result
 * e.g. consider a case where A/B testing would result in using different orders, colors, and texts,
 * when the component receives the variation after first render, it might cause a flash in the component.
 * In cases like these we might want to fetch the variation before the first render of the component.
 *
 * This hook will NOT push the GA event (use `useExperiment`)
 */ function usePrepareExperiment(experiment, userId1) {
    const [status, setStatus] = useState(()=>{
        var ref;
        if (!userId1) {
            return experimentError(new Error('ERR_USER_ID'));
        }
        const cache = (ref = experimentCache[userId1]) === null || ref === void 0 ? void 0 : ref[experiment];
        if (cache) {
            return cache;
        }
        return experimentLoading();
    });
    function updateStatus(userId, result) {
        // updating the cache
        if (!experimentCache[userId]) {
            experimentCache[userId] = {};
        }
        experimentCache[userId][experiment] = result;
        // updating internal state
        setStatus(result);
    }
    function fetchExperiment(userId) {
        return _fetchExperiment.apply(this, arguments);
    }
    function _fetchExperiment() {
        _fetchExperiment = _asyncToGenerator(function*(userId) {
            updateStatus(userId, experimentLoading());
            try {
                const apiUrl = window.location.origin;
                const { variation  } = yield Optimizely.decide(apiUrl, {
                    userId,
                    experiment
                });
                updateStatus(userId, experimentSuccess(variation));
            } catch (error) {
                updateStatus(userId, experimentError(error));
            }
        });
        return _fetchExperiment.apply(this, arguments);
    }
    useEffect(()=>{
        if (userId1) {
            var ref;
            // check if experiment should be disabled
            if (shouldDisableExperiments()) {
                return updateStatus(userId1, experimentError(new Error('ERR_DISABLED')));
            }
            const state = (ref = experimentCache[userId1]) === null || ref === void 0 ? void 0 : ref[experiment];
            if (state && !state.isLoading) {
                setStatus(state);
            } else {
                fetchExperiment(userId1);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        userId1,
        experiment
    ]);
    return status;
}

/**
 * useExperiment hook will return the experiment variation!
 * This hook will also push the GA event
 *
 * If you need to pre-fetch the result before first render of the component,
 *  use `usePrepareExperiment` somewhere in parent components tree
 */ function useExperiment(experiment, userId) {
    const result = usePrepareExperiment(experiment, userId);
    useEffect(()=>{
        if (shouldDisableExperiments()) {
            return;
        }
        if (!result.isLoading && userId) {
            if (result.error) {
                // check if experiment has been deactivated
                if (result.error.message !== 'ACTIVATION_FAILED') {
                    trackExperiment(userId, experiment, 'error');
                }
            } else {
                trackExperiment(userId, experiment, result.variation);
            }
        }
    }, [
        userId,
        experiment,
        result
    ]);
    return result;
}

export { useExperiment, usePrepareExperiment };
//# sourceMappingURL=index.js.map
